import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { Inventory } from '@mui/icons-material';

const UpdateInventory = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    quantity: '',
    unit: '',
    min_quantity: '',
    cost_per_unit: '',
    supplier: '',
    expiry_date: '',
    notes: ''
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.item_name || !formData.category || !formData.quantity || !formData.unit) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.quantity) < 0) {
      setError('Quantity must be a positive number');
      return;
    }

    // Create new inventory item object
    const newInventoryItem = {
      id: Date.now(), // Simple ID generation for demo
      item_name: formData.item_name,
      category: formData.category,
      quantity: parseFloat(formData.quantity),
      unit: formData.unit,
      min_quantity: parseFloat(formData.min_quantity) || 0,
      cost_per_unit: parseFloat(formData.cost_per_unit) || 0,
      supplier: formData.supplier,
      expiry_date: formData.expiry_date,
      notes: formData.notes,
      last_updated: new Date().toISOString().split('T')[0]
    };

    onAdd(newInventoryItem);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      item_name: '',
      category: '',
      quantity: '',
      unit: '',
      min_quantity: '',
      cost_per_unit: '',
      supplier: '',
      expiry_date: '',
      notes: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory color="primary" />
          <Typography variant="h6">Add Inventory Item</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={6}>
              <TextField
                name="item_name"
                label="Item Name *"
                value={formData.item_name}
                onChange={handleChange}
                fullWidth
                required
                placeholder="e.g., Layer Feed, Multivitamins"
              />
            </Grid>

            <Grid size={6}>
              <FormControl fullWidth required>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category *"
                >
                  <MenuItem value="Feed">Feed</MenuItem>
                  <MenuItem value="Medicine">Medicine</MenuItem>
                  <MenuItem value="Supplies">Supplies</MenuItem>
                  <MenuItem value="Equipment">Equipment</MenuItem>
                  <MenuItem value="Vitamins">Vitamins</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={4}>
              <TextField
                name="quantity"
                label="Quantity *"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>

            <Grid size={4}>
              <TextField
                name="unit"
                label="Unit *"
                value={formData.unit}
                onChange={handleChange}
                fullWidth
                required
                placeholder="kg, liters, pieces, bottles"
              />
            </Grid>

            <Grid size={4}>
              <TextField
                name="min_quantity"
                label="Minimum Quantity"
                type="number"
                value={formData.min_quantity}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, step: 0.1 }}
                placeholder="Alert threshold"
              />
            </Grid>

            <Grid size={6}>
              <TextField
                name="cost_per_unit"
                label="Cost Per Unit"
                type="number"
                value={formData.cost_per_unit}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="Unit cost"
              />
            </Grid>

            <Grid size={6}>
              <TextField
                name="supplier"
                label="Supplier"
                value={formData.supplier}
                onChange={handleChange}
                fullWidth
                placeholder="Supplier name"
              />
            </Grid>

            <Grid size={6}>
              <TextField
                name="expiry_date"
                label="Expiry Date"
                type="date"
                value={formData.expiry_date}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional notes about the item..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Add Item
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateInventory;
